import React, { useEffect } from 'react';

import classNames from 'classnames';
import dayjs from 'dayjs';
import styled from 'styled-components';

import Button from 'components/lib/Button';
import { isManagedBySuperuser } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';
import { SnapshotDataType } from 'types/snapshot';

import SnapshotActorTooltip from './SnapshotActorTooltip';
import { useSnapshotContext } from './SnapshotContext';
import SnapshotSelectorMenuItemContent from './SnapshotSelectorMenuItemContent';

const MenuContainer = styled.div`
  display: flex;
  justify-content: end;
  height: 100%;
`;

const PrimaryMenu = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const SecondaryMenu = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-left: 1px solid var(--line-gray);
  height: 100%;
`;

const MenuActions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  padding: 10px;
`;

const MenuItem = styled.div`
  display: flex;
  padding: 8px 10px;
  cursor: pointer;
  align-items: center;
  border-bottom: 1px solid var(--line-gray);
  gap: 4px;

  &.selected {
    background-color: var(--button-blue);
    color: white;
  }

  &:not(.selected):hover {
    background-color: var(--background-light-blue);
  }
`;

interface Props {
  showSnapshots: boolean;
  selectedSnapshot: SnapshotDataType | undefined;
  setShowSnapshots: (value: boolean) => void;
  setSelectedSnapshot: (value: SnapshotDataType | undefined) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

const SnapshotSelectorMenu = ({
  showSnapshots,
  setShowSnapshots,
  selectedSnapshot,
  setSelectedSnapshot,
  onConfirm,
  onCancel,
}: Props) => {
  const { currentUser } = useAppSelector((state) => state.auth);
  const { snapshot, currentSnapshot, historicalSnapshots, draftSnapshots } = useSnapshotContext();

  useEffect(() => {
    if (currentSnapshot) {
      const allSnapshots = [currentSnapshot, ...(historicalSnapshots ?? []), ...(draftSnapshots ?? [])];
      const item = allSnapshots?.find((item: SnapshotDataType) => item.uuid === snapshot?.uuid);
      if (item) {
        setSelectedSnapshot(item);
      }
    }
  }, [snapshot]);

  const dateFormat = isManagedBySuperuser(currentUser) ? 'MMM DD YYYY' : 'MMM YYYY';
  return (
    <MenuContainer data-test-id="cd-community-snapshot-selector-menu">
      <PrimaryMenu data-test-id="cd-community-snapshot-selector-primary-list">
        {currentSnapshot && (
          <SnapshotActorTooltip
            snapshot={currentSnapshot}
            showTooltip={Boolean(currentUser?.isSuperuser || isManagedBySuperuser(currentUser))}
          >
            <MenuItem
              data-test-id="cd-community-snapshot-selector-item-current"
              className={classNames({
                selected: currentSnapshot?.uuid === selectedSnapshot?.uuid && !showSnapshots,
              })}
              onClick={() => {
                setShowSnapshots(false);
                setSelectedSnapshot(currentSnapshot);
              }}
            >
              <span>Current Data</span>
              <span>·</span>
              {currentSnapshot && <span>{dayjs(currentSnapshot.date).format(dateFormat)}</span>}
            </MenuItem>
          </SnapshotActorTooltip>
        )}
        {draftSnapshots && draftSnapshots.length > 0 && (
          <SnapshotActorTooltip
            snapshot={draftSnapshots[0]}
            showTooltip={Boolean(currentUser?.isSuperuser || isManagedBySuperuser(currentUser))}
          >
            <MenuItem
              data-test-id="cd-community-snapshot-selector-item-draft"
              className={classNames({ selected: draftSnapshots[0].uuid === selectedSnapshot?.uuid })}
              onClick={() => {
                setShowSnapshots(false);
                setSelectedSnapshot(draftSnapshots[0]);
              }}
            >
              Draft data (ongoing shop)
            </MenuItem>
          </SnapshotActorTooltip>
        )}
        {Boolean(historicalSnapshots?.length) && (
          <MenuItem
            data-test-id="cd-community-snapshot-selector-item-historical"
            className={classNames({ selected: showSnapshots })}
            onClick={() => setShowSnapshots(true)}
          >
            Historical Data
          </MenuItem>
        )}
        {!showSnapshots && (
          <SnapshotMenuActions disabled={snapshot === selectedSnapshot} onConfirm={onConfirm} onCancel={onCancel} />
        )}
      </PrimaryMenu>
      {showSnapshots && Boolean(historicalSnapshots?.length) && (
        <SecondaryMenu data-test-id="cd-community-snapshot-selector-historical-list">
          <div>
            {historicalSnapshots?.map((item: SnapshotDataType) => (
              <MenuItem
                key={item.uuid}
                data-test-id={`cd-community-snapshot-selector-item-historical-${item.uuid}`}
                className={classNames({ selected: item?.uuid === selectedSnapshot?.uuid })}
                onClick={() => setSelectedSnapshot(item)}
              >
                <SnapshotSelectorMenuItemContent item={item} />
              </MenuItem>
            ))}
          </div>
          <SnapshotMenuActions
            disabled={snapshot?.uuid === selectedSnapshot?.uuid}
            onConfirm={onConfirm}
            onCancel={onCancel}
          />
        </SecondaryMenu>
      )}
    </MenuContainer>
  );
};

interface ActionProps {
  disabled: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const SnapshotMenuActions = ({ disabled, onConfirm, onCancel }: ActionProps) => (
  <MenuActions data-test-id="cd-community-snapshot-selector-actions">
    <Button
      data-test-id="cd-community-snapshot-selector-apply"
      disabled={disabled}
      style={{ height: 40, width: 120 }}
      type={'primary'}
      onClick={onConfirm}
    >
      Apply
    </Button>
    <Button data-test-id="cd-community-snapshot-selector-cancel" style={{ height: 40, width: 120 }} onClick={onCancel}>
      Cancel
    </Button>
  </MenuActions>
);

export default SnapshotSelectorMenu;
